import { IF, IS, nil, NO, UIColor, UICore, UIRectangle, UITextView, UIView, UIViewBroadcastEvent, YES } from "uicore-ts"
import { RHBadges, RHColors, RHNavbar } from "./Utils_slop/RHDesignTokens"


UIView.injectCSS(
    `
    .UICore_UIView_RHFloatingMenuRowView {
        outline: none !important;
        transition: background-color 0.1s ease, color 0.1s ease;
    }
    .UICore_UIView_RHFloatingMenuRowView:hover,
    .UICore_UIView_RHFloatingMenuRowView:focus-visible {
        background-color: ${RHNavbar.userMenuItemHoverBackgroundColor} !important;
    }
    .UICore_UIView_RHFloatingMenuRowView:hover,
    .UICore_UIView_RHFloatingMenuRowView:hover *,
    .UICore_UIView_RHFloatingMenuRowView:focus-visible,
    .UICore_UIView_RHFloatingMenuRowView:focus-visible * {
        color: ${RHNavbar.userMenuItemHoverTextColor} !important;
    }
    `,
    "rh-floating-menu-row-interaction"
)


export interface RHFloatingMenuItem {
    title: string
    isActive?: boolean
    isDestructive?: boolean
    action: () => void | Promise<void>
}


export type RHFloatingMenuViewConfiguration = {
    elementID?: string
    hasTail?: boolean
}


class RHFloatingMenuActiveIndicatorView extends UIView {

    readonly dotView = new UIView().configuredWithObject({
        backgroundColor: RHColors.successGreen,
        style: { borderRadius: "50%" }
    }).addedAsSubviewToView(this)


    override layoutSubviews() {
        super.layoutSubviews()
        this.dotView.frame = this.contentBounds
            .rectangleWithWidth(RHBadges.notificationDotSize, 0.5)
            .rectangleWithHeight(RHBadges.notificationDotSize, 0.5)
    }

}


class RHFloatingMenuRowView extends UIView {

    readonly activeIndicatorView = new RHFloatingMenuActiveIndicatorView()
        .addedAsSubviewToView(this)

    readonly titleLabel = new UITextView().configuredWithObject({
        backgroundColor: UIColor.transparentColor,
        isSingleLine: YES,
        userInteractionEnabled: NO,
        textColor: new UIColor(RHNavbar.userMenuItemTextColor),
        style: {
            fontFamily: RHNavbar.userMenuFontFamily,
            fontSize: RHNavbar.userMenuItemFontSize,
            fontWeight: RHNavbar.userMenuItemFontWeight,
            lineHeight: `${RHNavbar.userMenuItemHeight}px`,
            whiteSpace: "nowrap"
        }
    }).addedAsSubviewToView(this)


    constructor(item: RHFloatingMenuItem, isLast: boolean) {
        super()

        this.titleLabel.text = item.title
        this.activeIndicatorView.hidden = item.isActive !== YES
        if (item.isDestructive === YES) {
            this.titleLabel.textColor = RHColors.dangerTextSoft
        }

        const floatingMenuRowView: UIView = this
        floatingMenuRowView.configureWithObject({
            backgroundColor: UIColor.transparentColor,
            style: {
                cursor: "pointer",
                borderBottom: IF(isLast)(() =>
                    "none"
                ).ELSE(() =>
                    `1px solid ${RHNavbar.userMenuItemSeparatorColor}`
                )
            },
            controlEventTargetAccumulator: {
                PointerUpInside: () => {
                    void item.action()
                }
            }
        })
        this.viewHTMLElement.setAttribute("role", "menuitem")
        this.viewHTMLElement.tabIndex = -1
    }


    intrinsicTitleWidth(): number {
        let width = this.titleLabel.intrinsicContentWidth(RHNavbar.userMenuItemHeight)
        if (!this.activeIndicatorView.hidden) {
            width += RHBadges.notificationDotSize + RHBadges.notificationDotMarginLeft
        }
        return width
    }


    override layoutSubviews() {
        super.layoutSubviews()
        this.contentBoundsWithInsets(
            RHNavbar.userMenuItemHorizontalInset,
            RHNavbar.userMenuItemHorizontalInset,
            0,
            0
        )
            .IF(!this.activeIndicatorView.hidden)
            .distributeViewsAlongWidth(
                [this.activeIndicatorView, this.titleLabel],
                undefined,
                RHBadges.notificationDotMarginLeft,
                [RHBadges.notificationDotSize, nil]
            )
            .ELSE()
            .assignedAsFrameOfView(this.titleLabel)
            .ENDIF()
    }

}


/**
 * RH-styled floating menu shared by anchored page actions and top-bar menus.
 * It owns viewport-aware placement, dismissal, keyboard navigation and focus
 * restoration. Callers choose whether the panel has a speech-bubble tail.
 */
export class RHFloatingMenuView extends UIView {

    readonly _backdropView = new UIView().configuredWithObject({
        backgroundColor: UIColor.transparentColor,
        controlEventTargetAccumulator: {
            PointerUpInside: () => this.dismiss(NO)
        }
    })

    readonly _tailView = new UIView().configuredWithObject({
        backgroundColor: new UIColor(RHNavbar.userMenuPanelBackgroundColor),
        style: {
            border: `1px solid ${RHNavbar.userMenuPanelBorderColor}`
        }
    })

    readonly hasTail: boolean
    _rowViews: RHFloatingMenuRowView[] = []
    _items: RHFloatingMenuItem[] = []
    _triggerView?: UIView
    _keyboardHandler?: (sender: UIView, event: Event) => void
    _scrollHandler?: (sender: UIView, event: Event) => void
    _scrollRootView?: UIView
    _focusedIndex = -1


    constructor(configuration: RHFloatingMenuViewConfiguration = {}) {
        super(configuration.elementID)

        this.hasTail = configuration.hasTail !== NO
        const floatingMenuView: UIView = this
        floatingMenuView.configureWithObject({
            backgroundColor: new UIColor(RHNavbar.userMenuPanelBackgroundColor),
            style: {
                borderRadius: RHNavbar.userMenuPanelBorderRadius,
                boxShadow: RHNavbar.userMenuPanelShadow,
                border: `1px solid ${RHNavbar.userMenuPanelBorderColor}`,
                outline: "none",
                overflow: "hidden",
                zIndex: RHNavbar.userMenuPanelZIndex
            }
        })
        this._tailView.frameTransform = "rotate(45deg)"
        this.viewHTMLElement.setAttribute("role", "menu")
        this.viewHTMLElement.tabIndex = -1
    }


    get isPresented(): boolean {
        return IS(this.superview)
    }


    presentFromTriggerView(triggerView: UIView, items: RHFloatingMenuItem[]) {
        const rootView = UICore.main.rootViewController.view
        const triggerRectangle = triggerView.viewHTMLElement.getBoundingClientRect()
        const rootRectangle = rootView.viewHTMLElement.getBoundingClientRect()
        const pageScale = UIView.pageScale || 1
        const anchorFrame = rootView.bounds
            .rectangleWithX((triggerRectangle.left - rootRectangle.left) / pageScale)
            .rectangleWithY((triggerRectangle.top - rootRectangle.top) / pageScale)
            .rectangleWithWidth(triggerRectangle.width / pageScale)
            .rectangleWithHeight(triggerRectangle.height / pageScale)

        this._presentInHostView(rootView, anchorFrame, items, triggerView)
    }


    presentInHostView(hostView: UIView, anchorFrame: UIRectangle, items: RHFloatingMenuItem[]) {
        this._presentInHostView(hostView, anchorFrame, items)
    }


    _presentInHostView(
        hostView: UIView,
        anchorFrame: UIRectangle,
        items: RHFloatingMenuItem[],
        triggerView?: UIView
    ) {
        this.dismiss(NO)
        if (items.length === 0) {
            return
        }

        this._triggerView = triggerView
        this._triggerView?.viewHTMLElement.setAttribute("aria-expanded", "true")
        this._items = items
        this._rowViews = items.map((item, index) =>
            new RHFloatingMenuRowView(
                {
                    title: item.title,
                    isActive: item.isActive,
                    isDestructive: item.isDestructive,
                    action: () => this._activateItem(index)
                },
                index === items.length - 1
            ).addedAsSubviewToView(this)
        )

        const panelWidth = this._panelWidth()
        const panelHeight = items.length * RHNavbar.userMenuItemHeight
        const panelFrame = this._panelFrameForAnchorFrameInHostView(
            anchorFrame,
            hostView,
            panelWidth,
            panelHeight
        )

        this._backdropView.frame = hostView.bounds.rectangleWithHeight(hostView.rootView.bounds.height)
        this._backdropView.addedAsSubviewToView(hostView)

        if (this.hasTail) {
            const tailSize = RHNavbar.userMenuTailSize
            this._tailView.frame = panelFrame
                .rectangleWithWidth(tailSize, 1)
                .rectangleByAddingX(-RHNavbar.userMenuTailRightInset)
                .rectangleWithHeight(tailSize, 0)
                .rectangleByAddingY(-tailSize / 2)
            this._tailView.addedAsSubviewToView(hostView)
        }

        this.frame = panelFrame
        this.addedAsSubviewToView(hostView)
        this.setNeedsLayout()

        this._keyboardHandler = (_sender, event) => this._handleKeyboardEvent(event as KeyboardEvent)
        this.addTargetForControlEvents([
            UIView.controlEvent.EscDown,
            UIView.controlEvent.DownArrowDown,
            UIView.controlEvent.UpArrowDown,
            UIView.controlEvent.EnterDown,
            UIView.controlEvent.SpaceDown
        ], this._keyboardHandler)
        this.viewHTMLElement.onkeydown = event => {
            if (event.key === "Home" || event.key === "End") {
                this._handleKeyboardEvent(event)
            }
        }
        this._scrollHandler = () => this.dismiss(NO)
        this._scrollRootView = hostView.rootView
        this._scrollRootView.addTargetForControlEvent(
            UIView.controlEvent.ScrollWheel,
            this._scrollHandler
        )
        this._focusedIndex = -1
        if (triggerView?.viewHTMLElement.matches(":focus-visible") === YES) {
            this._focusedIndex = 0
            this._rowViews.firstElement?.focus()
        }
        else {
            this.focus()
        }
    }


    _panelFrameForAnchorFrameInHostView(
        anchorFrame: UIRectangle,
        hostView: UIView,
        panelWidth: number,
        panelHeight: number
    ): UIRectangle {
        const hostRectangle = hostView.viewHTMLElement.getBoundingClientRect()
        const pageScale = UIView.pageScale || 1
        const viewportLeft = -hostRectangle.left / pageScale
        const viewportTop = -hostRectangle.top / pageScale
        const viewportRight = viewportLeft + window.innerWidth / pageScale
        const viewportBottom = viewportTop + window.innerHeight / pageScale
        const viewportInset = RHNavbar.userMenuAnchorGap
        let panelX = anchorFrame.max.x - panelWidth
        let panelY = anchorFrame.max.y + RHNavbar.userMenuAnchorGap

        panelX = Math.max(
            viewportLeft + viewportInset,
            Math.min(panelX, viewportRight - panelWidth - viewportInset)
        )
        if (panelY + panelHeight > viewportBottom - viewportInset) {
            panelY = anchorFrame.y - panelHeight - RHNavbar.userMenuAnchorGap
        }
        panelY = Math.max(
            viewportTop + viewportInset,
            Math.min(panelY, viewportBottom - panelHeight - viewportInset)
        )

        return hostView.bounds
            .rectangleWithX(panelX)
            .rectangleWithY(panelY)
            .rectangleWithWidth(panelWidth)
            .rectangleWithHeight(panelHeight)
    }


    _activateItem(index: number) {
        const item = this._items[index]
        if (!item) {
            return
        }
        this.dismiss(NO)
        void item.action()
    }


    _handleKeyboardEvent(event: KeyboardEvent) {
        if (!this.isPresented) {
            return
        }
        if (event.key === "Escape") {
            event.preventDefault()
            this.dismiss()
            return
        }
        if (event.key === "Home") {
            event.preventDefault()
            this._focusedIndex = 0
        }
        else if (event.key === "End") {
            event.preventDefault()
            this._focusedIndex = Math.max(0, this._rowViews.length - 1)
        }
        else if (event.key === "ArrowDown") {
            event.preventDefault()
            if (this._focusedIndex < 0) {
                this._focusedIndex = 0
            }
            else {
                this._focusedIndex = (this._focusedIndex + 1) % this._rowViews.length
            }
        }
        else if (event.key === "ArrowUp") {
            event.preventDefault()
            if (this._focusedIndex < 0) {
                this._focusedIndex = this._rowViews.length - 1
            }
            else {
                this._focusedIndex =
                    (this._focusedIndex - 1 + this._rowViews.length) % this._rowViews.length
            }
        }
        else if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            if (this._focusedIndex < 0) {
                return
            }
            this._activateItem(this._focusedIndex)
            return
        }
        else {
            return
        }
        this._rowViews[this._focusedIndex]?.focus()
    }


    dismiss(shouldRestoreFocus: boolean = YES) {
        const triggerView = this._triggerView
        triggerView?.viewHTMLElement.setAttribute("aria-expanded", "false")
        if (shouldRestoreFocus) {
            triggerView?.focus()
        }

        this._rowViews.forEach(rowView => rowView.removeFromSuperview())
        this._rowViews = []
        this._items = []
        this._tailView.removeFromSuperview()
        this._backdropView.removeFromSuperview()
        this.removeFromSuperview()

        if (this._keyboardHandler) {
            this.removeTargetForControlEvents([
                UIView.controlEvent.EscDown,
                UIView.controlEvent.DownArrowDown,
                UIView.controlEvent.UpArrowDown,
                UIView.controlEvent.EnterDown,
                UIView.controlEvent.SpaceDown
            ], this._keyboardHandler)
            this._keyboardHandler = undefined
        }
        this.viewHTMLElement.onkeydown = null
        if (this._scrollHandler && this._scrollRootView) {
            this._scrollRootView.removeTargetForControlEvent(
                UIView.controlEvent.ScrollWheel,
                this._scrollHandler
            )
            this._scrollHandler = undefined
            this._scrollRootView = undefined
        }
        this._triggerView = undefined
    }


    _panelWidth(): number {
        const widestLabel = this._rowViews
            .map(rowView => rowView.intrinsicTitleWidth())
            .max()
        const contentWidth = widestLabel + RHNavbar.userMenuItemHorizontalInset * 2
        return [
            [contentWidth, RHNavbar.userMenuPanelMinWidth].max(),
            RHNavbar.userMenuPanelMaxWidth
        ].min()
    }


    override didReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.didReceiveBroadcastEvent(event)

        if (event.name === UICore.broadcastEventName.WindowDidResize) {
            this.dismiss()
        }
    }


    override layoutSubviews() {
        super.layoutSubviews()
        this.bounds.framesByDistributingViewsAsColumn(
            this._rowViews,
            0,
            this._rowViews.map(() => RHNavbar.userMenuItemHeight)
        )
    }

}
